import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  CheckCircle, 
  FileCheck, 
  Users, 
  Eye, 
  Plus, 
  Award,
  Search,
  Filter,
  TrendingUp
} from 'lucide-react';

// Import existing verification components
import VerificationRequestManager from './VerificationRequestManager';
import VerificationProposalWorkspace from './VerificationProposalWorkspace';
import ProjectVerificationDashboard from './ProjectVerificationDashboard';
import MultiVerifierSystem from './MultiVerifierSystem';
import VerifierMarketplace from '../verifier/VerifierMarketplace';
import VerifierDirectory from '../verifiers/VerifierDirectory';

const UnifiedVerification = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  
  // Mock user state - replace with actual auth context
  const [user] = useState({
    isAuthenticated: true,
    name: 'John Doe',
    email: 'john@example.com',
    role: 'project_owner', // 'project_owner', 'verifier', 'buyer', 'admin'
    avatar: null
  });

  const getTabsConfig = () => {
    const baseTabs = [
      { id: 'overview', label: 'Verification Hub', icon: CheckCircle },
      { id: 'directory', label: 'Find Verifiers', icon: Users },
      { id: 'requests', label: 'Verification Requests', icon: FileCheck }
    ];

    // Add role-specific tabs
    if (user.role === 'project_owner') {
      baseTabs.push(
        { id: 'dashboard', label: 'My Verifications', icon: TrendingUp },
        { id: 'multi-verifier', label: 'Manage Verifiers', icon: Award }
      );
    } else if (user.role === 'verifier') {
      baseTabs.push(
        { id: 'marketplace', label: 'Find Projects', icon: Eye },
        { id: 'workspace', label: 'My Workspace', icon: Award }
      );
    }

    return baseTabs;
  };

  const tabs = getTabsConfig();

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return <VerificationOverview user={user} navigate={navigate} />;
      case 'directory':
        return <VerifierDirectory />;
      case 'requests':
        return <VerificationRequestManager />;
      case 'dashboard':
        return <ProjectVerificationDashboard />;
      case 'multi-verifier':
        return <MultiVerifierSystem />;
      case 'marketplace':
        return <VerifierMarketplace />;
      case 'workspace':
        return <VerificationProposalWorkspace />;
      default:
        return <VerificationOverview user={user} navigate={navigate} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Verification Center</h1>
              <p className="text-gray-600 mt-1">
                Comprehensive platform for carbon credit project verification
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <button className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center">
                <Plus className="h-4 w-4 mr-2" />
                {user.role === 'verifier' ? 'Submit Proposal' : 'Request Verification'}
              </button>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-green-500 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="h-5 w-5 mr-2" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        {renderTabContent()}
      </div>
    </div>
  );
};

// Verification Overview Component
const VerificationOverview = ({ user, navigate }) => {
  const [searchTerm, setSearchTerm] = useState('');

  const quickActions = user.role === 'verifier' 
    ? [
        { 
          title: 'Browse Projects', 
          description: 'Find carbon credit projects seeking verification',
          icon: Eye, 
          color: 'blue',
          onClick: () => navigate('/verification/marketplace')
        },
        { 
          title: 'Submit Proposal', 
          description: 'Respond to verification requests',
          icon: Plus, 
          color: 'green',
          onClick: () => navigate('/verification/requests')
        },
        { 
          title: 'My Workspace', 
          description: 'Manage active verification projects',
          icon: Award, 
          color: 'purple',
          onClick: () => navigate('/verification/workspace')
        },
        { 
          title: 'Verifier Network', 
          description: 'Connect with other verifiers',
          icon: Users, 
          color: 'orange',
          onClick: () => navigate('/verifiers')
        }
      ]
    : [
        { 
          title: 'Find Verifiers', 
          description: 'Browse qualified verification specialists',
          icon: Users, 
          color: 'blue',
          onClick: () => navigate('/verifiers')
        },
        { 
          title: 'Request Verification', 
          description: 'Submit your project for verification',
          icon: Plus, 
          color: 'green',
          onClick: () => navigate('/verification/requests')
        },
        { 
          title: 'Verification Status', 
          description: 'Track your project verification progress',
          icon: TrendingUp, 
          color: 'purple',
          onClick: () => navigate('/verification/dashboard')
        },
        { 
          title: 'Multi-Verifier Setup', 
          description: 'Manage multiple verifiers for enhanced credibility',
          icon: Award, 
          color: 'orange',
          onClick: () => navigate('/verification/multi-verifier')
        }
      ];

  const verificationStandards = [
    { name: 'Verra VCS', projects: 156, icon: '🌿' },
    { name: 'Gold Standard', projects: 89, icon: '🏆' },
    { name: 'Climate Action Reserve', projects: 67, icon: '🛡️' },
    { name: 'American Carbon Registry', projects: 45, icon: '📋' }
  ];

  const recentActivity = [
    { action: 'New verification request submitted', project: 'Solar Farm Kenya', time: '2 hours ago', status: 'pending' },
    { action: 'Verification completed', project: 'Forest Conservation Brazil', time: '1 day ago', status: 'approved' },
    { action: 'Proposal submitted', project: 'Wind Energy India', time: '2 days ago', status: 'review' },
    { action: 'Multi-verifier setup completed', project: 'Biomass Plant Mexico', time: '3 days ago', status: 'verified' }
  ];

  return (
    <div className="space-y-8">
      {/* Search Bar */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Search projects, verifiers, or standards..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>
          <button className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
            <Filter className="h-4 w-4 mr-2" />
            Filters
          </button>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {quickActions.map((action, index) => (
          <div
            key={index}
            onClick={action.onClick}
            className="bg-white rounded-lg shadow hover:shadow-md transition-shadow cursor-pointer border-l-4 border-l-green-500"
          >
            <div className="p-6">
              <div className="flex items-start space-x-4">
                <div className={`w-12 h-12 bg-${action.color}-100 rounded-lg flex items-center justify-center`}>
                  <action.icon className={`h-6 w-6 text-${action.color}-600`} />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{action.title}</h3>
                  <p className="text-sm text-gray-600">{action.description}</p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Verification Standards */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Verification Standards</h3>
          <p className="text-sm text-gray-600">Supported carbon credit standards</p>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {verificationStandards.map((standard, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4 hover:border-green-300 transition-colors">
                <div className="flex items-center space-x-3">
                  <span className="text-2xl">{standard.icon}</span>
                  <div>
                    <h4 className="font-medium text-gray-900">{standard.name}</h4>
                    <p className="text-sm text-gray-600">{standard.projects} projects</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
          <p className="text-sm text-gray-600">Latest verification updates</p>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            {recentActivity.map((activity, index) => (
              <div key={index} className="flex items-center space-x-4 p-3 hover:bg-gray-50 rounded-lg">
                <div className={`w-2 h-2 rounded-full ${
                  activity.status === 'approved' ? 'bg-green-500' :
                  activity.status === 'pending' ? 'bg-yellow-500' :
                  activity.status === 'review' ? 'bg-blue-500' :
                  'bg-purple-500'
                }`}></div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">{activity.action}</p>
                  <p className="text-sm text-gray-600">{activity.project}</p>
                </div>
                <span className="text-xs text-gray-400">{activity.time}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnifiedVerification; 